import { FC } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useTranslation } from 'react-i18next';
import { Alert, Button } from 'react-daisyui';
import { Link } from 'react-router-dom';
import Routes from '@/router/Routes.ts';
import * as z from 'zod';
import { useAuthContext } from '@/context/AuthContext.tsx';
import LoadingButton from '@/components/functional/LoadingButton/LoadingButton.tsx';
import BrandCardSideContent from '@/components/ui/BrandCardSideContent/BrandCardSideContent.tsx';
import TextInput from '@/components/functional/TextInput/TextInput.tsx';

const loginSchema = z.object({
  email: z.string().min(1, { message: 'This field is required.' }).email('Invalid email'),
  password: z.string().min(1, { message: 'This field is required.' }),
});

type LoginSchema = z.infer<typeof loginSchema>;

const LoginPage: FC = () => {
  const { control, handleSubmit } = useForm<LoginSchema>({
    resolver: zodResolver(loginSchema),
    defaultValues: {
      email: '',
      password: '',
    },
  });
  const { t } = useTranslation();
  const {
    loginQuery: { mutateAsync: login, isPending: isLoadingLogin, error: errorLogin, isError: isErrorLogin },
  } = useAuthContext();

  const handleLogin = (data: LoginSchema) => {
    void (async () => {
      try {
        if (isLoadingLogin) return;

        await login({
          email: data.email,
          password: data.password,
        });
      } catch (error) {
        console.error(error);
      }
    })();
  };

  return (
    <BrandCardSideContent>
      <form className="w-[428px] max-w-full flex flex-col gap-5" onSubmit={handleSubmit(handleLogin)}>
        <div className="flex flex-col gap-2">
          <h2 className="text-2xl font-varela font-normal leading-[133%]">Welcome to Past Paper Plus</h2>
          <p className="text-text-secondary text-sm leading-[143%] tracking-[0.17px]">Fill in information about you</p>
        </div>
        <div className="flex flex-col gap-4">
          {isErrorLogin && <Alert status="error">{errorLogin?.message}</Alert>}
          <Controller
            control={control}
            name="email"
            render={({ field: { onChange, value }, formState: { errors } }) => (
              <TextInput
                error={errors.email?.message}
                value={value}
                onChange={onChange}
                label={t('label.email')}
                placeholder={t('placeholder.email')}
              />
            )}
          />
          <Controller
            control={control}
            name="password"
            render={({ field: { onChange, value }, formState: { errors } }) => (
              <TextInput
                error={errors.password?.message}
                value={value}
                onChange={onChange}
                type={'password'}
                label={t('label.password')}
                placeholder={t('placeholder.password')}
              />
            )}
          />
          <div className="flex justify-end">
            <Link to={Routes.FORGOT_PASSWORD}>
              <Button type="button" size="sm" color="ghost" className="font-semibold">
                {t('button.forgotPassword')} ?
              </Button>
            </Link>
          </div>
          <LoadingButton loading={isLoadingLogin} type="submit" color="primary">
            {t('button.login')}
          </LoadingButton>
        </div>
        <div className="flex justify-end">
          <Link to={Routes.REGISTER}>
            <Button type="button" color="ghost">{t('button.createNewAccount')}</Button>
          </Link>
        </div>
      </form>
    </BrandCardSideContent>
  );
};

export default LoginPage;
